import React, { useState } from 'react';

import { css } from '../Viewer/lut';
import { DIFF_COLORS, DERIVED_DEFAULTS } from '../Viewer/difference';
import { CardTitle, Slider } from './ChannelCard';

// Canal calculé (src/components/Viewer/difference.js), présenté comme une modalité : interrupteur (exclusif, comme
// les autres), opération et images A / B, gain, fond, masque ; repliable comme les autres cartes.
export default function DerivedChannelCard({
  channels, a, b, settings, active, error, onChange, onToggle, onAutoBalance, collapsed, onCollapse, label, onRename,
}) {
  const [balancing, setBalancing] = useState(false);
  const autoBalance = async () => {
    setBalancing(true);
    try { await onAutoBalance(); } finally { setBalancing(false); }
  };
  const name = label || (settings.op === 'diff' ? 'Difference A − B' : 'Ratio A / B');
  // Seuil affiché dans l'unité d'origine du canal choisi (comme sur sa carte), en % pour la moyenne.
  const thresholdLabel = t => {
    const ch = settings.mask === 'a' ? a : settings.mask === 'b' ? b : null;
    if (!ch) return `${Math.round(t * 100)}%`;
    const [lo, hi] = ch.contrast;
    const raw = lo + (ch.inverted ? 1 - t : t) * (hi - lo);  // carte inversée : pixels gardés sous le seuil
    return `${ch.inverted ? '≤ ' : ''}${Math.round(ch.toOriginal ? ch.toOriginal(raw) : raw)} (${settings.mask.toUpperCase()})`;
  };
  const select = (key, value) => (
    <select value={value?.id || ''} aria-label={`Modality ${key.toUpperCase()}`}
      onChange={e => onChange({ [key]: e.target.value })}>
      {channels.map(ch => <option key={ch.id} value={ch.id}>{ch.name}{ch.inverted ? ' (inverted)' : ''}</option>)}
    </select>
  );
  return (
    <div className={`channel derived${active ? '' : ' off'}${collapsed ? ' collapsed' : ''}`}>
      <div className="channel-header">
        <span className="swatch derived" title="Computed channel" />
        <CardTitle name={name} collapsed={collapsed} onCollapse={onCollapse} onRename={onRename} />
        <label className="switch" title="Show (hides the other modalities)">
          <input type="checkbox" checked={active} onChange={e => onToggle(e.target.checked)} />
          <span />
        </label>
      </div>
      {!collapsed && (
        <div className="channel-body compare">
          <div className="compare-row">
            <span className="compare-label">Operation</span>
            <select value={settings.op} aria-label="Operation" onChange={e => onChange({ op: e.target.value })}>
              <option value="ratio">Ratio A / B</option>
              <option value="diff">Difference A − B</option>
            </select>
          </div>
          <div className="compare-row">
            <span className="compare-key" style={{ background: css(DIFF_COLORS.a) }}>A</span>{select('a', a)}
          </div>
          <div className="compare-row">
            <span className="compare-key" style={{ background: css(DIFF_COLORS.b) }}>B</span>{select('b', b)}
          </div>
          {error && <p className="viewer-note error">{error}</p>}
          <Slider label="Gain (amplifies the differences)" value={settings.gain} min={1} max={20} step={0.5}
            reset={DERIVED_DEFAULTS.gain} format={v => `× ${v}`} onChange={v => onChange({ gain: v })} />
          <Slider label="B balance (overall intensity offset)" value={settings.balance ?? 0} min={-2} max={2}
            reset={0} format={v => `B × ${(2 ** v).toLocaleString('en-US', { maximumFractionDigits: 2 })}`}
            onChange={v => onChange({ balance: v })} />
          <div className="channel-options">
            <button type="button" className="chip" onClick={autoBalance} disabled={balancing || !!error}
              title="B is scaled so that A and B have the same median on the tissue kept by the mask (redo it if the contrast of A or B changes)">
              {balancing ? 'Computing…' : 'Auto balance'}
            </button>
          </div>
          <Slider label="Anti-aliasing smoothing (σ, screen px)" value={settings.smooth ?? DERIVED_DEFAULTS.smooth} max={4}
            step={0.25} reset={DERIVED_DEFAULTS.smooth}
            format={v => (v ? `σ ${v.toLocaleString('en-US')} px` : 'none')} onChange={v => onChange({ smooth: v })} />
          <Slider label="Background (shared structure)" value={settings.context} max={1} reset={DERIVED_DEFAULTS.context}
            onChange={v => onChange({ context: v })} />
          <div className="compare-row">
            <span className="compare-label">Mask on</span>
            <select value={settings.mask} aria-label="Mask on" onChange={e => onChange({ mask: e.target.value })}>
              <option value="mean">Mean of A and B</option>
              <option value="a">A · {a?.name}</option>
              <option value="b">B · {b?.name}</option>
            </select>
          </div>
          <Slider label="Mask threshold" value={settings.threshold} max={1} step={0.005}
            reset={DERIVED_DEFAULTS.threshold} format={thresholdLabel} onChange={v => onChange({ threshold: v })} />
          <div className="channel-options">
            <label className={`chip${settings.maskPreview ? ' checked' : ''}`}>
              <input type="checkbox" checked={!!settings.maskPreview}
                onChange={e => onChange({ maskPreview: e.target.checked })} />
              Mask preview (white = kept)
            </label>
            <label className={`chip${settings.inverted ? ' checked' : ''}`} title="White background, same colors for A and B">
              <input type="checkbox" checked={!!settings.inverted}
                onChange={e => onChange({ inverted: e.target.checked })} />
              Invert
            </label>
          </div>
          <p className="compare-legend">
            <span><i style={{ background: css(DIFF_COLORS.a) }} /> A brighter</span>
            <span><i style={{ background: css(DIFF_COLORS.b) }} /> B brighter</span>
            <span><i style={{ background: settings.inverted ? '#fff' : '#000', borderColor: '#555' }} /> equal or masked</span>
          </p>
        </div>
      )}
    </div>
  );
}
